import React, { useState } from 'react';
import { Elements, useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { stripePromise } from '../../../stripe/stripe.js';
import '../../../component-styles/StripePaymentModal.css';
import API_BASE from '../../../utils/api.js';
import { roundCurrency, sanitizeCurrencyInput } from '../../../utils/currency.js';

function PaymentForm({
  shareToken,
  remainingBalance,
  defaultAmount,
  invoiceNumber,
  title,
  onSuccess,
  onClose,
  createIntentEndpoint,
  applyPaymentEndpoint,
  metadata,
  headers,
}) {
  const stripe = useStripe();
  const elements = useElements();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  // Prefilled with the requested deposit when one is still owed.
  const [customAmount, setCustomAmount] = useState(
    Number(defaultAmount) > 0 ? Number(defaultAmount).toFixed(2) : ''
  );

  const [paymentMethod, setPaymentMethod] = useState('card'); // 'card' | 'ach'
  const [achName, setAchName] = useState('');
  const [achEmail, setAchEmail] = useState('');
  const [achStep, setAchStep] = useState('input'); // 'input' | 'mandate'
  const [pendingClientSecret, setPendingClientSecret] = useState('');
  const [pendingIntentId, setPendingIntentId] = useState('');
  const [pendingFeeCents, setPendingFeeCents] = useState(0);

  const [feePreview, setFeePreview] = useState(null); // { amount, method, feeCents } | null
  const [previewingFee, setPreviewingFee] = useState(false);

  const invoiceLabel = invoiceNumber ? `Invoice #${invoiceNumber} payment` : 'Invoice payment';
  const hasBalanceCap = remainingBalance != null;

  const validateAmount = (val) => {
    const amt = roundCurrency(val);
    if (!amt || amt < 1) { setError('Amount must be at least $1'); return null; }
    if (hasBalanceCap && amt > remainingBalance) { setError(`Amount cannot exceed $${remainingBalance}`); return null; }
    return amt;
  };

  const createPaymentIntent = async (chargedAmount, pmType = 'card') => {
    const endpoint = createIntentEndpoint || `${API_BASE}/api/payments/create-payment-intent`;
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...headers },
      credentials: 'include',
      body: JSON.stringify({
        amount: chargedAmount,
        description: invoiceLabel,
        metadata: metadata || { shareToken },
        paymentMethodType: pmType,
      }),
    });
    const data = await res.json();
    if (!data.clientSecret) throw new Error('Could not initialize payment');
    return data;
  };

  // Previews the processing-fee surcharge (if the studio has one configured)
  // by creating — and immediately abandoning — a PaymentIntent for the
  // current amount/method, so the client sees the fee before they commit to
  // paying. Re-runs whenever the amount is finalized or the method changes.
  const refreshFeePreview = async (pmType = paymentMethod) => {
    const amt = roundCurrency(customAmount);
    if (!amt || amt < 1 || (hasBalanceCap && amt > remainingBalance)) {
      setFeePreview(null);
      return;
    }
    setPreviewingFee(true);
    try {
      const data = await createPaymentIntent(amt, pmType === 'ach' ? 'us_bank_account' : 'card');
      setFeePreview({ amount: amt, method: pmType, feeCents: Number(data.processingFeeCents || 0) });
    } catch {
      setFeePreview(null);
    } finally {
      setPreviewingFee(false);
    }
  };

  const activeFeePreview =
    feePreview && feePreview.amount === roundCurrency(customAmount) && feePreview.method === paymentMethod
      ? feePreview
      : null;
  const feeDollars = activeFeePreview ? round2ForDisplay(activeFeePreview.feeCents / 100) : 0;
  const totalWithFee = activeFeePreview ? round2ForDisplay(activeFeePreview.amount + feeDollars) : null;

  function round2ForDisplay(v) {
    return Math.round(Number(v || 0) * 100) / 100;
  }

  const recordPayment = async (chargedAmount, intentId) => {
    const endpoint = applyPaymentEndpoint
      || `${API_BASE}/api/invoices/public/${encodeURIComponent(shareToken)}/apply-payment`;
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...headers },
      credentials: 'include',
      body: JSON.stringify({ stripeIntentId: intentId, amount: chargedAmount }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.error || 'Payment was captured but recording it failed.');
    return data;
  };

  /* ------------------------------------------------------------------ */
  /* CARD FLOW                                                            */
  /* ------------------------------------------------------------------ */
  const handleCardSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    const amountToCharge = validateAmount(customAmount);
    if (!amountToCharge) return;

    setLoading(true);
    setError('');

    try {
      const { clientSecret } = await createPaymentIntent(amountToCharge, 'card');
      const card = elements.getElement(CardElement);
      const result = await stripe.confirmCardPayment(clientSecret, { payment_method: { card } });
      if (result.error) { setError(result.error.message); return; }
      const applyData = await recordPayment(amountToCharge, result.paymentIntent.id);
      setSuccess(true);
      onSuccess(amountToCharge, applyData);
    } catch (err) {
      setError(err?.message || 'Payment failed. Please try again.');
    }

    setLoading(false);
  };

  /* ------------------------------------------------------------------ */
  /* ACH FLOW — STEP 1: COLLECT BANK ACCOUNT                            */
  /* ------------------------------------------------------------------ */
  const handleAchCollect = async (e) => {
    e.preventDefault();
    if (!stripe) return;

    const amountToCharge = validateAmount(customAmount);
    if (!amountToCharge) return;
    if (!achName.trim()) { setError('Account holder name is required'); return; }
    if (!achEmail.trim()) { setError('Email address is required'); return; }

    setLoading(true);
    setError('');

    try {
      const { clientSecret, processingFeeCents } = await createPaymentIntent(amountToCharge, 'us_bank_account');
      setPendingFeeCents(Number(processingFeeCents || 0));

      const { paymentIntent, error: collectError } = await stripe.collectBankAccountForPayment({
        clientSecret,
        params: {
          payment_method_type: 'us_bank_account',
          payment_method_data: {
            billing_details: { name: achName.trim(), email: achEmail.trim() },
          },
        },
      });

      if (collectError) { setError(collectError.message); return; }

      if (paymentIntent?.status === 'requires_confirmation') {
        setPendingClientSecret(clientSecret);
        setPendingIntentId(paymentIntent.id);
        setAchStep('mandate');
      } else {
        setError('Bank account connection was cancelled. Please try again.');
      }
    } catch (err) {
      setError(err?.message || 'Failed to connect bank account. Please try again.');
    }

    setLoading(false);
  };

  /* ------------------------------------------------------------------ */
  /* ACH FLOW — STEP 2: CONFIRM WITH MANDATE ACCEPTANCE                 */
  /* ------------------------------------------------------------------ */
  const handleAchConfirm = async () => {
    if (!stripe || !pendingClientSecret) return;
    setLoading(true);
    setError('');

    try {
      const { paymentIntent, error: confirmError } = await stripe.confirmUsBankAccountPayment(pendingClientSecret);

      if (confirmError) {
        if (confirmError.payment_intent?.status === 'requires_payment_method') {
          setAchStep('input');
          setError('Bank account verification failed. Please try a different account.');
        } else {
          setError(confirmError.message);
        }
        return;
      }

      const amountToCharge = roundCurrency(customAmount);

      // ACH debits confirm to "processing", not "succeeded" — they settle
      // 3-5 business days later, so the backend won't accept a synchronous
      // apply-payment call yet. The payment_intent.succeeded webhook credits
      // the document once the debit actually clears.
      if (paymentIntent?.status === 'succeeded') {
        const applyData = await recordPayment(amountToCharge, paymentIntent.id);
        setSuccess(true);
        onSuccess(amountToCharge, applyData);
      } else if (paymentIntent?.status === 'processing') {
        setSuccess(true);
        onSuccess(amountToCharge, null);
      } else {
        setError('Bank account connection was cancelled. Please try again.');
      }
    } catch (err) {
      setError(err?.message || 'Payment confirmation failed. Please try again.');
    }

    setLoading(false);
  };

  const switchMethod = (method) => {
    setPaymentMethod(method);
    setError('');
    setAchStep('input');
    setPendingClientSecret('');
    refreshFeePreview(method);
  };

  const feePreviewLine = activeFeePreview && feeDollars > 0 && (
    <p className="spm-fee-preview" style={{ fontSize: 13, color: '#6b7280', margin: '4px 0 0' }}>
      + Processing fee: ${feeDollars.toFixed(2)} — you'll be charged ${totalWithFee.toFixed(2)}
    </p>
  );

  const depositHintLine = Number(defaultAmount) > 0 && hasBalanceCap && Number(defaultAmount) < remainingBalance && (
    <p style={{ fontSize: 13, color: '#6b7280', margin: '4px 0 0' }}>
      Deposit due: ${Number(defaultAmount).toFixed(2)}. You can pay up to the full balance of $
      {Number(remainingBalance).toFixed(2)}.
    </p>
  );

  /* ------------------------------------------------------------------ */
  /* RENDER                                                               */
  /* ------------------------------------------------------------------ */
  return (
    <div className="spm-overlay">
      <div className="spm-modal">
        <h2>{title || 'Pay Invoice'}</h2>

        {success ? (
          <div className="spm-success">
            <p>
              {paymentMethod === 'ach'
                ? 'ACH payment initiated! Funds typically settle in 3–5 business days.'
                : 'Payment successful!'}
            </p>
            <button className="spm-pay-btn" onClick={onClose}>Done</button>
          </div>
        ) : (
          <>
            {/* Method Tabs */}
            <div className="spm-method-tabs">
              <button
                type="button"
                className={`spm-method-tab${paymentMethod === 'card' ? ' active' : ''}`}
                onClick={() => switchMethod('card')}
              >
                Card
              </button>
              <button
                type="button"
                className={`spm-method-tab${paymentMethod === 'ach' ? ' active' : ''}`}
                onClick={() => switchMethod('ach')}
              >
                ACH Bank Transfer
              </button>
            </div>

            {/* CARD FORM */}
            {paymentMethod === 'card' && (
              <form onSubmit={handleCardSubmit}>
                <div className="spm-field">
                  <label>Payment Amount</label>
                  <input
                    type="number"
                    min="1"
                    step="0.01"
                    max={hasBalanceCap ? remainingBalance : undefined}
                    placeholder={hasBalanceCap ? `Max: $${remainingBalance}` : 'Enter amount'}
                    value={customAmount}
                    onChange={(e) => setCustomAmount(sanitizeCurrencyInput(e.target.value))}
                    onBlur={() => refreshFeePreview('card')}
                    className="spm-amount-input"
                  />
                  {depositHintLine}
                  {feePreviewLine}
                </div>
                <div className="spm-field">
                  <label>Card Information</label>
                  <CardElement className="spm-card-element" />
                </div>
                {error && <p className="spm-error">{error}</p>}
                <button type="submit" className="spm-pay-btn" disabled={!stripe || loading}>
                  {loading ? 'Processing…' : 'Submit Payment'}
                </button>
                <button type="button" className="spm-cancel" onClick={onClose}>Cancel</button>
              </form>
            )}

            {/* ACH FORM — STEP 1 */}
            {paymentMethod === 'ach' && achStep === 'input' && (
              <form onSubmit={handleAchCollect}>
                <div className="spm-field">
                  <label>Payment Amount</label>
                  <input
                    type="number"
                    min="1"
                    step="0.01"
                    max={hasBalanceCap ? remainingBalance : undefined}
                    placeholder={hasBalanceCap ? `Max: $${remainingBalance}` : 'Enter amount'}
                    value={customAmount}
                    onChange={(e) => setCustomAmount(sanitizeCurrencyInput(e.target.value))}
                    onBlur={() => refreshFeePreview('ach')}
                    className="spm-amount-input"
                  />
                  {depositHintLine}
                  {feePreviewLine}
                </div>
                <div className="spm-field">
                  <label>Account Holder Name</label>
                  <input
                    type="text"
                    placeholder="Full name"
                    value={achName}
                    onChange={(e) => setAchName(e.target.value)}
                    className="spm-amount-input"
                    required
                  />
                </div>
                <div className="spm-field">
                  <label>Email Address</label>
                  <input
                    type="email"
                    placeholder="your@email.com"
                    value={achEmail}
                    onChange={(e) => setAchEmail(e.target.value)}
                    className="spm-amount-input"
                    required
                  />
                </div>
                <p className="spm-ach-note">
                  You'll be prompted to securely connect your bank account via Stripe.
                </p>
                {error && <p className="spm-error">{error}</p>}
                <button type="submit" className="spm-pay-btn" disabled={!stripe || loading}>
                  {loading ? 'Connecting…' : 'Connect Bank Account'}
                </button>
                <button type="button" className="spm-cancel" onClick={onClose}>Cancel</button>
              </form>
            )}

            {/* ACH FORM — STEP 2: MANDATE */}
            {paymentMethod === 'ach' && achStep === 'mandate' && (
              <div>
                <div className="spm-ach-mandate">
                  <p>
                    By clicking <strong>Confirm Payment</strong>, you authorize the debit of your
                    bank account via the ACH network for{' '}
                    <strong>${(roundCurrency(customAmount) + pendingFeeCents / 100).toFixed(2)}</strong>
                    {pendingFeeCents > 0 && ` (including a $${(pendingFeeCents / 100).toFixed(2)} processing fee)`}.
                    This authorization remains in effect until revoked with 30 days' written notice.
                    You may be contacted if any issues arise with your payment.
                  </p>
                </div>
                {error && <p className="spm-error">{error}</p>}
                <button
                  type="button"
                  className="spm-pay-btn"
                  onClick={handleAchConfirm}
                  disabled={loading}
                >
                  {loading ? 'Confirming…' : 'Confirm Payment'}
                </button>
                <button
                  type="button"
                  className="spm-cancel"
                  onClick={() => { setAchStep('input'); setPendingClientSecret(''); }}
                >
                  Back
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default function PublicStripePaymentModal(props) {
  return (
    <Elements stripe={stripePromise}>
      <PaymentForm {...props} />
    </Elements>
  );
}

